import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { mockEndpoints } from "../mocks/appDetails";
import { generateClient } from "aws-amplify/api";
import { GQL_QUERIES } from "../queries";
import { resultsTransformer } from "../util/queryResultsTransformers";
import { GqlErrorTransformer } from "../util/GqlErrorTransformer";
import { connectChannel, getOnQueryStarted } from './event-channels';

const client = generateClient();

export const graphqlApi = createApi({
    reducerPath: "graphqlApi",
    baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
    endpoints: (builder) => ({
        // Homepage
        //
        getDashboardData: builder.query({ queryFn: async () => await getDataByQueryName("getDashboardDetails"), }),
        getNextApplicationForReviewer: builder.mutation({ queryFn: async variables => await getDataByQueryName("getNextApplicationForReviewer", variables), }),
        // TODO Delete mock stuff: getStudentDashboardData: builder.query({ queryFn: async (input) => mockEndpoints.getStudentDashboardData.queryFn(input), }),
        // TODO Delete mock stuff: getSearchResults: builder.query({ queryFn: async (searchParam) => await mockEndpoints.search.queryFn(searchParam), }),

        // Applicant Details
        getApplicationDetails: builder.query({
            //queryFn is just a promise, so normally we would hit an API.post call. in this case we just build a dummy promise that we can replace later with an API.post
            // queryFn: (variables) => mockEndpoints.studentDetails.queryFn(variables), 
            // queryFn: async (variables) => edmDocumentEvents(mockEndpoints.studentDetails.queryFn(variables)),
            queryFn: async (variables) => await getDataByQueryName("getApplicationDetails", variables),
            // async onQueryStarted(variables, { dispatch, queryFulfilled }) {
            async onQueryStarted(variables, { dispatch, getState }) {
                connectChannel('/documents/fetch', dispatch, getState);
                // const key = `${variables.applicantEmployeeId}-${variables.programId}-${variables.term}`;
                // recommendationHistoryListener(key);
            },
            providesTags: ["getApplicationDetails"],
            // queryFn: async (variables) => await edmDocumentEvents(await getDataByQueryName("getApplicationDetails", variables)),
        }),
        getDocuments: builder.query({ queryFn: async (variables) => await getDataByQueryName("fetchDocumentUrl", variables), }),
        getDocumentStatus: builder.query({ queryFn: async (variables) => await getDataByQueryName("getDocumentStatus", variables), }),
        searchDocuments: builder.query({ queryFn: async (variables) => await getDataByQueryName("searchApplicationDocument", variables), }),
        applicationRestartReview     : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationRestartReview", variables), }),
        applicationAssignReview      : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationAssignReview", variables), }),
        applicationReassignReview    : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationReassignReview", variables), }),
        applicationUnassignReview    : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationUnassignReview", variables), }),
        applicationSubmitReview      : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationSubmitReview", variables), }),
        applicationSaveReview        : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationSaveReview", variables), }),
        applicationConflictOfInterest: builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationConflictOfInterest", variables), }),
        adminOverride                : builder.mutation({ queryFn: async variables => await getDataByQueryName("adminOverride", variables), }),

        // Manage Applications
        getManageApplicationSidebarProgram: builder.query({
            queryFn: async () => await getDataByQueryName("getManageApplicationsSidebar"),
            providesTags: ["ManageApplicationsSidebarProgram"],
        }),
        getManageApplicationSidebarNumbersReviewer: builder.query({
            queryFn: (reviewerEmployeeId) => mockEndpoints.manageApplicationsSidebarReviewer.queryFn(reviewerEmployeeId),
            providesTags: ["ManageApplicationsSidebarReviewer"],
        }),
        listAssignableApplications: builder.query({
            queryFn: async (val) => await getDataByQueryName("listAssignableApplications", { input: val }),
            providesTags: ["list-assignable"],
        }),
        listAssignedApplications: builder.query({
            queryFn: async (val) => await getDataByQueryName("listAssignedApplications", { input: val }),
            providesTags: ["list-assigned"],
        }),
        bulkAssignUnassignApplications: builder.mutation({ queryFn: async variables => await getDataByQueryName("bulkAssignUnassignApplications", variables), }),

        // Reports
        getReport: builder.query({
            queryFn: async (variables) => await getDataByQueryName("getReport", variables),
            providesTags: ["getReport"],
        }),
        getAdminOverrideReasons: builder.query({ queryFn: async () => await getDataByQueryName("getAdminOverrideReasons"), }),
        approveRecommendations: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("approveRecommendations", variables),
            onQueryStarted: getOnQueryStarted('/reports/recommendations-approved'),
        }),
        submitStudentRequestProgramChangeWithdraw: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("submitStudentRequestProgramChangeWithdraw", variables),
            onQueryStarted: getOnQueryStarted('/reports/program-change-or-withdraw-complete'),
        }),
        publishApplications: builder.mutation({
            queryFn: async variables => await getDataByQueryName("publishApplications", variables),
            onQueryStarted: getOnQueryStarted('/reports/publish-complete'),
        }),
        approveDenyRequests: builder.mutation({ queryFn: async variables => await getDataByQueryName("approveDenyRequests", variables), }),

        // Reviewers // TODO Save to the actual database
        getReviewers                : builder.query   ({ queryFn: async (variables) => await getDataByQueryName("getReviewers", variables), providesTags: ["Reviewers"] }),
        getOneReviewer              : builder.query   ({ queryFn: async (employeeId) => await getDataByQueryName("getOneReviewer", {employeeId}), providesTags: ["Reviewer"], }),
        updateReviewer              : builder.mutation({ queryFn: async (variables) => await getDataByQueryName("updateReviewer", variables), invalidatesTags: ["Reviewer"], }),
        toggleActiveStatus          : builder.mutation({ queryFn: async (variables) => await getDataByQueryName("toggleActiveStatus", variables), invalidatesTags: ["Reviewers"], }),
        
        // Configurations
        getCriteria                 : builder.query   ({ queryFn: async () => await getDataByQueryName("getCriteria"), }),
        saveCriteria                : builder.mutation({ queryFn: async (variables) => await getDataByQueryName("saveCriteria", {rules: variables}), }),
        getRubrics                  : builder.query   ({ queryFn: async programId => await getDataByQueryName("getRubrics", {programId}), }),
        getRubricConfig             : builder.query   ({ queryFn: async () => await getDataByQueryName("getRubricConfig"), }),
        setActiveRubric             : builder.mutation({ queryFn: async variables => await getDataByQueryName("setActiveRubric", variables), }),
        createARubric               : builder.mutation({ queryFn: async programId => await getDataByQueryName("createARubric", {programId}), }),
        cloneRubric                 : builder.mutation({ queryFn: async variables => await getDataByQueryName("cloneRubric", variables), }),
        archiveRubric               : builder.mutation({ queryFn: async variables => await getDataByQueryName("archiveRubric", variables), }),
        deleteRubric                : builder.mutation({ queryFn: async variables => await getDataByQueryName("deleteRubric", variables), }),
        saveRubricV2                : builder.mutation({ queryFn: async variables => await getDataByQueryName("saveRubricV2", variables), }),

        // Exports
        exportGrid: builder.query({
            queryFn: async (variables) => await getDataByQueryName("exportGrid", variables),
            async onQueryStarted(variables, { dispatch }) {
                connectChannel('/documents/export', dispatch);
            },
        }),
        // Search - Basic & Advanced
        getApplicantAttributes      : builder.query   ({ queryFn: async () => await getDataByQueryName("getApplicantAttributes"), }),
        getChangeWorkflowOptions    : builder.query   ({ queryFn: async variables => await getDataByQueryName("getChangeWorkflowOptions", variables), }),
        getDistinctValuesForSearch  : builder.query   ({ queryFn: async attribute => await getDataByQueryName("getDistinctValuesForSearch", {attribute}), }),
        getProgramResolveOptions    : builder.query   ({ queryFn: async () => await getDataByQueryName("getProgramResolveOptions"), }),
        getStudentRequestReasons    : builder.query   ({ queryFn: async () => await getDataByQueryName("getStudentRequestReasons"), }),
        personSearch                : builder.query   ({ queryFn: async variables => await getDataByQueryName("personSearch", variables), }),
        searchApplicant             : builder.query   ({ queryFn: async employeeId => await getDataByQueryName("searchApplicant", {employeeId}), }),
        searchReviewer              : builder.query   ({ queryFn: async employeeId => await getDataByQueryName("searchReviewer", {employeeId}), }),
        searchForAllData            : builder.query   ({ queryFn: async variables => await getDataByQueryName("searchForAllData", variables), }),
        overrideWorkflowDecision    : builder.mutation({ queryFn: async variables => await getDataByQueryName("overrideWorkflowDecision", variables),
            onQueryStarted: getOnQueryStarted('/search/override-workflow-decision-complete'),
        }),
        changeWorkflow              : builder.mutation({ queryFn: async variables => await getDataByQueryName("changeWorkflow", variables),
            onQueryStarted: getOnQueryStarted('/search/change-workflow-complete'),
        }),
        changeRequested             : builder.mutation({ queryFn: async variables => await getDataByQueryName("changeRequested", variables), }),
        
        // Admissions Cycles Configuration // TODO Save to the actual database
        getManageCycles: builder.query({ queryFn: () => mockEndpoints.manageCycles.queryFn(), }),

        // System Wide Messages
        getSystemWideMessages: builder.query({
            queryFn: async () => await getDataByQueryName("getSystemWideMessages"),
            providesTags: ["systemMessages"],
        }),
        dismissSystemWideMessages: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("dismissSystemWideMessages", variables),
            invalidatesTags: ["systemMessages"],
        }),
    }),
});

// RTKQ auto-unwraps from data, still need to unwrap inner queryname wrapper though
async function getDataByQueryName(queryName, variables) {
    if (!GQL_QUERIES[queryName]) { throw `queryName '${queryName}' not found, please check imports`; } // eslint-disable-line
    const queryParameters = { query: GQL_QUERIES[queryName], variables };
    
    const apiCallAsFn = async () => (await client.graphql(queryParameters)).data[queryName];
    const dataFromApi = await GqlErrorTransformer(apiCallAsFn); // Execute API call, and capture+throw transformed error results
    const dataTransformed = resultsTransformer(dataFromApi, queryName);
    return { data: dataTransformed };
}

export const {
  // Dashboard
  useGetDashboardDataQuery, useLazyGetDashboardDataQuery, useGetStudentDashboardDataQuery, useGetSearchResultsQuery, useGetNextApplicationForReviewerMutation,
  
  // Application Details
  useGetApplicationDetailsQuery,
  useGetDocumentsQuery, useLazyGetDocumentsQuery, useSearchDocumentsQuery,
  useApplicationRestartReviewMutation,
  useApplicationAssignReviewMutation, useApplicationReassignReviewMutation, useApplicationUnassignReviewMutation,
  useApplicationSubmitReviewMutation,
  useApplicationSaveReviewMutation,
  useApplicationConflictOfInterestMutation,
  useAdminOverrideMutation,
  
  // Manage Applications
  useGetManageApplicationSidebarProgramQuery,
  useListAssignableApplicationsQuery, useListAssignedApplicationsQuery,
  useBulkAssignUnassignApplicationsMutation,
  
  // Reports
  useGetReportQuery, useLazyGetReportQuery,
  useApproveRecommendationsMutation,
  useSubmitStudentRequestProgramChangeWithdrawMutation,
  usePublishApplicationsMutation,
  useApproveDenyRequestsMutation,
  
  // Reviewers
  useGetReviewersQuery, useToggleActiveStatusMutation,
  
  // Reviewer
  useGetOneReviewerQuery, useUpdateReviewerMutation,

  // Rubric Config
  useGetRubricsQuery, useLazyGetRubricsQuery, useGetRubricConfigQuery, useCreateARubricMutation, useArchiveRubricMutation, useSaveRubricV2Mutation, 
  useDeleteRubricMutation, useCloneRubricMutation, useSetActiveRubricMutation,
  useGetCriteriaQuery, useLazyGetCriteriaQuery, useSaveCriteriaMutation,

  // System Wide Messages
  useGetSystemWideMessagesQuery, useDismissSystemWideMessagesMutation,

  // Cycles
  useGetManageCyclesQuery,

  // Search
  useLazySearchApplicantQuery, useSearchApplicantQuery,
  useLazySearchReviewerQuery, useSearchReviewerQuery,
  useLazySearchForAllDataQuery, useSearchForAllDataQuery,
  useGetApplicantAttributesQuery, useGetDistinctValuesForSearchQuery,
  useGetStudentRequestReasonsQuery,
  usePersonSearchQuery, useLazyPersonSearchQuery,
  useGetProgramResolveOptionsQuery, useGetAdminOverrideReasonsQuery,
  useChangeRequestedMutation,

  // Exports
  useExportGridQuery, useLazyExportGridQuery,
  useChangeWorkflowMutation, useGetChangeWorkflowOptionsQuery,
  useOverrideWorkflowDecisionMutation,
  
} = graphqlApi;


export const useGetApplicationDetailsQueryState = graphqlApi.endpoints.getApplicationDetails.useQueryState;
